import { useDeferredValue, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  AlertTriangle,
  CalendarCheck2,
  CalendarX2,
  CheckCircle2,
  ChevronRight,
  Pencil,
  Search,
  ShieldAlert,
  Trash2,
} from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import type { AlunoTurmaResumo } from "@/hooks/useAlunosTurma";
import { cn } from "@/lib/utils";

type FiltroAlunos = "todos" | "faltou_hoje" | "atencao" | "consecutivas";
type OrdenacaoAlunos = "criticidade" | "nome" | "frequencia";

interface AlunosTableProps {
  alunos: AlunoTurmaResumo[];
  onRemove: (aluno: AlunoTurmaResumo) => void;
  onEdit?: (alunoId: string) => void;
  canEdit?: boolean;
}

const PAGE_SIZE = 40;

const formatDateLocal = (dateStr?: string | null) => {
  if (!dateStr) return "—";
  if (dateStr.includes("/")) return dateStr;

  const parts = dateStr.split("-");
  if (parts.length === 3 && parts[0].length === 4) {
    const [year, month, day] = parts;
    return `${day.padStart(2, "0")}/${month.padStart(2, "0")}/${year}`;
  }

  return dateStr;
};

const getStatusInfo = (aluno: AlunoTurmaResumo) => {
  if (aluno.faltouHoje) {
    return {
      label: "Faltou hoje",
      icon: CalendarX2,
      className: "border-red-200 bg-red-50 text-red-700",
      accent: "border-l-red-500",
    };
  }

  if (aluno.faltaJustificadaHoje) {
    return {
      label: "Falta justificada",
      icon: CalendarCheck2,
      className: "border-blue-200 bg-blue-50 text-blue-700",
      accent: "border-l-blue-500",
    };
  }

  if (aluno.nivelAtencao === "critico") {
    return {
      label: "Frequência crítica",
      icon: ShieldAlert,
      className: "border-red-200 bg-red-50 text-red-700",
      accent: "border-l-red-500",
    };
  }

  if (aluno.nivelAtencao === "atencao") {
    return {
      label: "Em atenção",
      icon: AlertTriangle,
      className: "border-amber-200 bg-amber-50 text-amber-800",
      accent: "border-l-amber-500",
    };
  }

  if (aluno.nivelAtencao === "sem_dados") {
    return {
      label: "Sem dados recentes",
      icon: CalendarCheck2,
      className: "border-slate-200 bg-slate-50 text-slate-600",
      accent: "border-l-slate-300",
    };
  }

  return {
    label: "Frequência regular",
    icon: CheckCircle2,
    className: "border-emerald-200 bg-emerald-50 text-emerald-700",
    accent: "border-l-emerald-500",
  };
};

const FrequenciaValue = ({ aluno }: { aluno: AlunoTurmaResumo }) => {
  if (aluno.totalChamadas === 0) {
    return <span className="text-slate-400">Sem dados</span>;
  }

  return (
    <span
      className={cn(
        "font-semibold tabular-nums",
        aluno.frequencia < 75
          ? "text-red-700"
          : aluno.frequencia < 85
            ? "text-amber-700"
            : "text-emerald-700",
      )}
    >
      {aluno.frequencia}%
    </span>
  );
};

const AlunosTable = ({
  alunos,
  onRemove,
  onEdit,
  canEdit = false,
}: AlunosTableProps) => {
  const [search, setSearch] = useState("");
  const deferredSearch = useDeferredValue(search);
  const [filtro, setFiltro] = useState<FiltroAlunos>("todos");
  const [ordenacao, setOrdenacao] = useState<OrdenacaoAlunos>("criticidade");
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);

  const contadores = useMemo(
    () => ({
      todos: alunos.length,
      faltouHoje: alunos.filter(aluno => aluno.faltouHoje).length,
      atencao: alunos.filter(
        aluno => aluno.nivelAtencao === "critico" || aluno.nivelAtencao === "atencao",
      ).length,
      consecutivas: alunos.filter(aluno => aluno.faltasConsecutivas >= 3).length,
    }),
    [alunos],
  );

  const filteredAlunos = useMemo(() => {
    const termo = deferredSearch.trim().toLocaleLowerCase("pt-BR");
    const pesoNivel = {
      critico: 0,
      atencao: 1,
      regular: 2,
      sem_dados: 3,
    };

    return alunos
      .filter(aluno => {
        const correspondeBusca =
          !termo ||
          aluno.nome.toLocaleLowerCase("pt-BR").includes(termo) ||
          aluno.matricula.toLocaleLowerCase("pt-BR").includes(termo);

        if (!correspondeBusca) return false;
        if (filtro === "faltou_hoje") return aluno.faltouHoje;
        if (filtro === "atencao") {
          return aluno.nivelAtencao === "critico" || aluno.nivelAtencao === "atencao";
        }
        if (filtro === "consecutivas") return aluno.faltasConsecutivas >= 3;
        return true;
      })
      .sort((alunoA, alunoB) => {
        if (ordenacao === "nome") return alunoA.nome.localeCompare(alunoB.nome);
        if (ordenacao === "frequencia") {
          if (alunoA.totalChamadas === 0) return 1;
          if (alunoB.totalChamadas === 0) return -1;
          return alunoA.frequencia - alunoB.frequencia;
        }

        if (alunoA.faltouHoje !== alunoB.faltouHoje) return alunoA.faltouHoje ? -1 : 1;
        const nivel = pesoNivel[alunoA.nivelAtencao] - pesoNivel[alunoB.nivelAtencao];
        if (nivel !== 0) return nivel;
        if (alunoA.faltasConsecutivas !== alunoB.faltasConsecutivas) {
          return alunoB.faltasConsecutivas - alunoA.faltasConsecutivas;
        }
        return alunoA.nome.localeCompare(alunoB.nome);
      });
  }, [alunos, deferredSearch, filtro, ordenacao]);

  useEffect(() => {
    setVisibleCount(PAGE_SIZE);
  }, [deferredSearch, filtro, ordenacao]);

  const visibleAlunos = filteredAlunos.slice(0, visibleCount);
  const hasMore = visibleCount < filteredAlunos.length;

  const filtros: Array<{ id: FiltroAlunos; label: string; count: number }> = [
    { id: "todos", label: "Todos", count: contadores.todos },
    { id: "faltou_hoje", label: "Faltaram hoje", count: contadores.faltouHoje },
    { id: "atencao", label: "Em atenção", count: contadores.atencao },
    { id: "consecutivas", label: "3+ consecutivas", count: contadores.consecutivas },
  ];

  return (
    <div className="w-full">
      <div className="mb-4 space-y-3 rounded-xl border border-slate-200 bg-slate-50/80 p-3 sm:p-4">
        <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
          <div className="relative w-full md:max-w-md">
            <Search
              aria-hidden="true"
              className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400"
            />
            <Input
              type="search"
              placeholder="Buscar por nome ou matrícula"
              value={search}
              onChange={event => setSearch(event.target.value)}
              className="h-11 bg-white pl-9"
              aria-label="Buscar aluno por nome ou matrícula"
            />
          </div>

          <Select
            value={ordenacao}
            onValueChange={value => setOrdenacao(value as OrdenacaoAlunos)}
          >
            <SelectTrigger className="h-11 w-full bg-white md:w-[210px]" aria-label="Ordenar alunos">
              <SelectValue placeholder="Ordenar alunos" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="criticidade">Mais críticos primeiro</SelectItem>
              <SelectItem value="nome">Nome A–Z</SelectItem>
              <SelectItem value="frequencia">Menor frequência</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div className="flex flex-wrap gap-2" aria-label="Filtros da lista de alunos">
          {filtros.map(item => (
            <Button
              key={item.id}
              type="button"
              size="sm"
              variant={filtro === item.id ? "default" : "outline"}
              onClick={() => setFiltro(item.id)}
              className={cn(
                "h-9 rounded-full px-3",
                filtro === item.id && "bg-slate-900 text-white hover:bg-slate-800",
              )}
              aria-pressed={filtro === item.id}
            >
              {item.label}
              <span
                className={cn(
                  "ml-1 rounded-full px-1.5 text-xs tabular-nums",
                  filtro === item.id ? "bg-white/20" : "bg-slate-100 text-slate-600",
                )}
              >
                {item.count}
              </span>
            </Button>
          ))}
        </div>

        <p className="text-xs text-slate-500" aria-live="polite">
          Exibindo {Math.min(visibleAlunos.length, filteredAlunos.length)} de{" "}
          {filteredAlunos.length} alunos
        </p>
      </div>

      <div className="space-y-3 sm:hidden">
        {visibleAlunos.map(aluno => {
          const status = getStatusInfo(aluno);
          const StatusIcon = status.icon;

          return (
            <Card
              key={aluno.id}
              className={cn(
                "border-l-4 shadow-sm [contain-intrinsic-size:156px] [content-visibility:auto]",
                status.accent,
              )}
            >
              <CardContent className="p-4">
                <div className="flex items-start gap-3">
                  <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-violet-100 text-sm font-bold text-violet-700">
                    {aluno.nome.slice(0, 2).toUpperCase()}
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="truncate font-semibold text-slate-900">{aluno.nome}</p>
                    <p className="text-xs text-slate-500">Matrícula {aluno.matricula}</p>
                    <Badge
                      variant="outline"
                      className={cn("mt-2 gap-1 font-medium", status.className)}
                    >
                      <StatusIcon className="h-3.5 w-3.5" aria-hidden="true" />
                      {status.label}
                    </Badge>
                  </div>
                </div>

                <div className="mt-4 grid grid-cols-3 gap-2 rounded-lg bg-slate-50 p-3 text-center">
                  <div>
                    <p className="text-[10px] uppercase tracking-wide text-slate-500">Frequência</p>
                    <p className="mt-1 text-sm">
                      <FrequenciaValue aluno={aluno} />
                    </p>
                  </div>
                  <div className="border-x border-slate-200">
                    <p className="text-[10px] uppercase tracking-wide text-slate-500">Consecutivas</p>
                    <p className="mt-1 text-sm font-semibold tabular-nums text-slate-800">
                      {aluno.faltasConsecutivas}
                    </p>
                  </div>
                  <div>
                    <p className="text-[10px] uppercase tracking-wide text-slate-500">Última falta</p>
                    <p className="mt-1 text-xs font-medium text-slate-700">
                      {formatDateLocal(aluno.ultimaFalta)}
                    </p>
                  </div>
                </div>

                <div className="mt-3 flex items-center gap-2">
                  <Button asChild variant="outline" className="h-11 flex-1 justify-between">
                    <Link to={`/turmas/${aluno.turma_id}/alunos/${aluno.id}`}>
                      Ver histórico
                      <ChevronRight className="h-4 w-4" aria-hidden="true" />
                    </Link>
                  </Button>
                  {canEdit && onEdit && (
                    <Button
                      type="button"
                      variant="outline"
                      size="icon"
                      className="h-11 w-11 text-slate-600"
                      onClick={() => onEdit(aluno.id)}
                      aria-label={`Editar ${aluno.nome}`}
                    >
                      <Pencil className="h-4 w-4" />
                    </Button>
                  )}
                  {canEdit && (
                    <Button
                      type="button"
                      variant="outline"
                      size="icon"
                      className="h-11 w-11 border-red-200 text-red-600 hover:bg-red-50 hover:text-red-700"
                      onClick={() => onRemove(aluno)}
                      aria-label={`Excluir ${aluno.nome}`}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      <div className="hidden overflow-x-auto rounded-xl border border-slate-200 sm:block">
        <Table className="w-full text-sm">
          <TableHeader className="bg-slate-50">
            <TableRow>
              <TableHead>Aluno</TableHead>
              <TableHead>Situação</TableHead>
              <TableHead>Frequência · 30 dias</TableHead>
              <TableHead>Última falta</TableHead>
              <TableHead className="text-right">Ações</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {visibleAlunos.map(aluno => {
              const status = getStatusInfo(aluno);
              const StatusIcon = status.icon;

              return (
                <TableRow
                  key={aluno.id}
                  className={cn(
                    aluno.nivelAtencao === "critico" && "bg-red-50/30",
                    aluno.nivelAtencao === "atencao" && "bg-amber-50/30",
                  )}
                >
                  <TableCell>
                    <p className="font-medium text-slate-900">{aluno.nome}</p>
                    <p className="text-xs text-slate-500">Matrícula {aluno.matricula}</p>
                  </TableCell>
                  <TableCell>
                    <Badge
                      variant="outline"
                      className={cn("gap-1 whitespace-nowrap font-medium", status.className)}
                    >
                      <StatusIcon className="h-3.5 w-3.5" aria-hidden="true" />
                      {status.label}
                    </Badge>
                    {aluno.faltasConsecutivas >= 2 && (
                      <p className="mt-1 text-xs text-slate-500">
                        {aluno.faltasConsecutivas} faltas consecutivas
                      </p>
                    )}
                  </TableCell>
                  <TableCell>
                    <FrequenciaValue aluno={aluno} />
                    {aluno.totalChamadas > 0 && (
                      <p className="text-xs text-slate-500">
                        {aluno.faltas} faltas em {aluno.totalChamadas} registros
                      </p>
                    )}
                  </TableCell>
                  <TableCell>{formatDateLocal(aluno.ultimaFalta)}</TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-1">
                      <Button asChild variant="ghost" size="sm" className="text-violet-700">
                        <Link to={`/turmas/${aluno.turma_id}/alunos/${aluno.id}`}>
                          Histórico
                        </Link>
                      </Button>
                      {canEdit && onEdit && (
                        <Button
                          type="button"
                          onClick={() => onEdit(aluno.id)}
                          variant="ghost"
                          size="icon"
                          className="text-slate-600"
                          aria-label={`Editar ${aluno.nome}`}
                        >
                          <Pencil className="h-4 w-4" />
                        </Button>
                      )}
                      {canEdit && (
                        <Button
                          type="button"
                          onClick={() => onRemove(aluno)}
                          variant="ghost"
                          size="icon"
                          className="text-red-600 hover:bg-red-50 hover:text-red-700"
                          aria-label={`Excluir ${aluno.nome}`}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>

      {filteredAlunos.length === 0 && (
        <div className="rounded-xl border-2 border-dashed border-slate-200 py-12 text-center text-slate-500">
          <Search className="mx-auto mb-3 h-8 w-8 text-slate-300" aria-hidden="true" />
          <p className="font-medium text-slate-700">Nenhum aluno encontrado</p>
          <p className="mt-1 text-sm">Ajuste a busca ou os filtros selecionados.</p>
        </div>
      )}

      {hasMore && (
        <div className="mt-5 flex justify-center">
          <Button
            type="button"
            variant="outline"
            className="h-11 min-w-44"
            onClick={() => setVisibleCount(current => current + PAGE_SIZE)}
          >
            Carregar mais {Math.min(PAGE_SIZE, filteredAlunos.length - visibleCount)}
          </Button>
        </div>
      )}
    </div>
  );
};

export default AlunosTable;
